import { Navigate, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { API_BASE, SHEETS } from "../utils/api";
import { getAdminSession, canSeeAdminPanel } from "../utils/adminView";

// Test/bypass login number — exempt from the dealer-sheet check.
const BYPASS_PHONE = "9898989898";

// The stored `dealer` is usually the RAW dealers-sheet row, so the phone
// can live under many different column names. Check them all. We verify
// by PHONE (the actual identity) — never by GST, since a GST is shared by
// a whole firm and would wrongly pass numbers that aren't registered.
const PHONE_KEYS = [
  "phone",
  "Contact No",
  "Contact No ",
  "Contact No.",
  "Contact Number",
  "Contact Number ",
  "Mobile",
  "Mobile No",
  "Mobile Number",
  "Phone",
  "NUMBER",
  "Number",
  "Contact",
  "Userid",
  "GID",
];

function readDealer() {
  try {
    const raw = localStorage.getItem("dealer");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function digits10(v) {
  return String(v ?? "")
    .replace(/\D/g, "")
    .slice(-10);
}

function extractPhone(d) {
  if (!d || typeof d !== "object") return "";
  for (const k of PHONE_KEYS) {
    const p = digits10(d[k]);
    if (p.length === 10) return p;
  }
  return "";
}

// The app keys its per-dealer caches by the ACTUAL logged-in number, e.g.
// `dealerRates:v1:9876543210` / `firmsCache:v1:9876543210`. For sessions
// saved before `loginPhone` existed, this is the most reliable source of
// the real login number — the stored `dealer` row only holds the firm's
// registered contact, which would wrongly pass an unregistered login.
function phoneFromCacheKeys() {
  const prefixes = ["dealerRates:v1:", "firmsCache:v1:"];
  try {
    for (const pre of prefixes) {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(pre)) {
          const p = digits10(k.slice(pre.length));
          if (p.length === 10) return p;
        }
      }
    }
  } catch {
    /* ignore */
  }
  return "";
}

function forceLogout() {
  try {
    // Unregistered session — wipe the WHOLE localStorage so no auth keys
    // or cached dealer data survive. Forces a clean logout.
    localStorage.clear();
  } catch {
    // Fallback: at least drop the auth keys if clear() is blocked.
    try {
      localStorage.removeItem("dealer");
      localStorage.removeItem("role");
      localStorage.removeItem("loginPhone");
    } catch {
      /* ignore */
    }
  }
}

export default function ProtectedRoute({ allowedRoles = [] }) {
  const dealer = readDealer();
  const roleRaw = localStorage.getItem("role");
  const isLoggedIn = !!dealer && !!roleRaw;

  // Prefer the EXACT number the user logged in with. For older sessions
  // (no loginPhone), recover the real login number from the per-dealer
  // cache keys; only as a last resort fall back to the stored dealer row.
  const phone =
    digits10(localStorage.getItem("loginPhone")) ||
    phoneFromCacheKeys() ||
    extractPhone(dealer);

  // Set to true once we've confirmed this number is NOT in the dealers
  // sheet — such a session is force-logged-out.
  const [denied, setDenied] = useState(false);

  // Verify the logged-in NUMBER against the live dealers sheet. Any number
  // that isn't in the sheet gets logged out immediately.
  useEffect(() => {
    if (!isLoggedIn || !phone || phone === BYPASS_PHONE) return;
    let cancelled = false;
    (async () => {
      try {
        // NBD on the dealer picker: the number lives in the INFO sheet,
        // not the dealers sheet — verify it there.
        const session = getAdminSession();
        const nbdSelf = session?.scope === "nbd" && digits10(session.phone) === phone;
        const res = await fetch(
          nbdSelf ? `${API_BASE}/api/sheets/staff-dealers?phone=${phone}` : SHEETS.dealers(phone),
          { cache: "no-store" }
        );
        // Only act on a definitive response. Network / server errors →
        // fail open so a transient glitch never kicks out valid users.
        if (!res.ok) return;
        const data = await res.json().catch(() => null);
        const valid = nbdSelf ? data?.found === true : Array.isArray(data) && data.length > 0;
        if (!cancelled && !valid) {
          forceLogout();
          setDenied(true);
        }
      } catch {
        /* fail open on network error */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, phone]);

  if (!isLoggedIn || denied) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles.length > 0) {
    const userRole = String(roleRaw).toLowerCase();
    const normalizedAllowedRoles = allowedRoles.map((r) =>
      String(r).toLowerCase()
    );

    if (!normalizedAllowedRoles.includes(userRole)) {
      return <Navigate to="/login-success" replace />;
    }
    if (normalizedAllowedRoles.includes("admin") && !canSeeAdminPanel()) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <Outlet />;
}
